import { useState, useEffect, useRef, useCallback } from "react";
import { Product } from "~src/types/winningProducts";
import { getBestSellingProducts } from "~src/api/winningProducts";
import { BestSellingProductsFilters } from "~src/pages/winningProducts/Products";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { logError } from "~src/utils/logger";

interface UseInfiniteBestSellingProductsProps {
  pageSize: number;
  filters?: BestSellingProductsFilters;
  searchText?: string;
}

export const useInfiniteBestSellingProducts = ({
  pageSize,
  filters,
  searchText,
}: UseInfiniteBestSellingProductsProps) => {
  const [data, setData] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const pendingLoadRef = useRef(false);
  const initialLoadDone = useRef(false);
  const lastFiltersRef = useRef<string>(JSON.stringify(filters || {})); // Track last filters comparison
  const filtersRef = useRef(filters);
  const searchTextRef = useRef(searchText);
  const pageSizeRef = useRef(pageSize);

  // New explicit fetchData function for forced data refresh
  const fetchData = useCallback(async (forceReset = false) => {
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear any existing data
    setData([]);
    setCurrentPage(1);
    setHasMore(true);
    setLoading(true);
    pendingLoadRef.current = false;
    
    // Immediately set count to null to trigger "Searching for products..." message
    // and force UI to reset expectations - do this especially for filter clearing
    if (forceReset) {
      setCount(null);
    }
    
    try {
      // Create new AbortController
      abortControllerRef.current = new AbortController();

      const response = await getBestSellingProducts(
        1,
        pageSizeRef.current,
        filtersRef.current,
        searchTextRef.current,
        abortControllerRef.current.signal
      );

      const initialProducts = response.results || [];
      const totalCount = response.count || 0;

      // Force immediate state updates in the correct order
      // First update the count to ensure the UI headline updates
      setCount(totalCount);
      
      // Then update the rest of the state
      setData(initialProducts);
      setHasMore(initialProducts.length === pageSizeRef.current);
      setCurrentPage(2);

      // Update last filters string
      lastFiltersRef.current = JSON.stringify(filtersRef.current || {});

      // Return the count for external handling if needed
      return totalCount;
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }
      // Handle 404 as empty results, not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setData([]);
        setCount(0);
        setHasMore(false);
        lastFiltersRef.current = JSON.stringify(filtersRef.current || {});
        return 0;
      }
      logError(`Error in data fetch: ${err}`);
      setError(err instanceof Error ? err : new Error('Failed to load products'));
      Sentry.captureException(err);
      return 0;
    } finally {
      setLoading(false);
      pendingLoadRef.current = false;
    }
  // Remove all dependencies to avoid infinite loops
  }, []);

  // Fetch data on first mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      // Fetch fresh data from backend (Redis caching handles performance)
      fetchData(false);
      initialLoadDone.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update refs when props change
  useEffect(() => {
    filtersRef.current = filters;
    searchTextRef.current = searchText;
    pageSizeRef.current = pageSize;
  }, [filters, searchText, pageSize]);

  // Effect to fetch data when filters change
  useEffect(() => {
    if (!initialLoadDone.current) {
      return; // Skip on initial load
    }

    const newFiltersString = JSON.stringify(filters || {});
    if (lastFiltersRef.current !== newFiltersString) {
      lastFiltersRef.current = newFiltersString;
      fetchData(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), searchText, pageSize]);

  const loadMore = async () => {
    if (!hasMore || loading || isLoadingRef.current || pendingLoadRef.current) {
      return;
    }

    try {
      pendingLoadRef.current = true;
      isLoadingRef.current = true;
      setLoading(true);

      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController
      abortControllerRef.current = new AbortController();

      const response = await getBestSellingProducts(
        currentPage,
        pageSizeRef.current,
        filtersRef.current,
        searchTextRef.current,
        abortControllerRef.current.signal
      );

      const newProducts = response.results || [];
      const totalCount = response.count || 0;

      // First update count to ensure UI reflects the current state
      setCount(totalCount);

      const updatedData = [...data, ...newProducts];
      setData(updatedData);
      setHasMore(newProducts.length === pageSizeRef.current);
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      if (axios.isCancel(err)) {
        return;
      }
      // Handle 404 as no more results, not an error
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setHasMore(false);
        return;
      }
      logError(`Error loading more products: ${err}`);
      setError(err instanceof Error ? err : new Error('Failed to load products'));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      pendingLoadRef.current = false;
    }
  };
  
  return {
    data,
    count,
    loadMore,
    loading,
    error,
    hasMore,
    fetchData,
  };
};
